import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import { importSync } from '@embroider/macros';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { recordIdentifierFor } from '@warp-drive/core';
import cannot from 'ember-can/helpers/cannot';
import { t } from 'ember-intl';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not, or } from 'ember-truth-helpers';

import Entity from 'umg-ember-backend/components/page-builder/content-items/item/entity';
import Button from 'umg-ember-backend/components/ui/button';
import ContextMenu from 'umg-ember-backend/components/ui/context-menu';
import Checkbox from 'umg-ember-backend/components/ui/form/field/checkbox';
import { HiddenIndicator } from 'umg-ember-backend/components/ui/hidden-indicator';
import InfoBox from 'umg-ember-backend/components/ui/info-box';
import Modal from 'umg-ember-backend/components/ui/modal';
import { SelectedAttributesIndicator } from 'umg-ember-backend/components/ui/selected-attributes-indicator';
import { findRecord } from 'umg-ember-backend/data/builders/umg-rest';
import modelIcon from 'umg-ember-backend/helpers/model-icon';
import stopPropagation from 'umg-ember-backend/helpers/stop-propagation';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import styles from './styles.module.css';

const bem = getBem(styles);

export default class PageBuilderContentItemsItemComponent extends Component {
  // Services
  @service abilities;
  @service breadcrumb;
  @service history;
  @service intl;
  @service recordCopy;
  @service store;

  // Defaults
  @tracked showErrors = false;
  @storeOnRoute('routeStorageKey') selectedEntity = null;
  @storeOnRoute('routeStorageKey') selectedContentItemToReplace = null;
  @storeOnRoute('routeStorageKey') scrollPositionEntity;
  @storeOnRoute('routeStorageKey') scrollPositionReplace;

  // Getter and setter
  get routeStorageKey() {
    return recordIdentifierFor(this.args.contentItem).lid;
  }

  get contentItemTypeName() {
    let contentItemType = this.args.contentItem.type;

    return this.intl.t(`models.${contentItemType}`);
  }

  get ContentItem() {
    let componentName = 'default';
    if (this.hasChildren) {
      componentName = this.args.contentItem.type;
    }

    const module = importSync(`./for/${componentName}`);

    return module.default;
  }

  get RecordExplorerForReplace() {
    const type = this.selectedContentItemToReplace?.type ?? 'article';

    const module = importSync(`../../../record-explorer/for/${type}`);

    return module.default;
  }

  get hasChildren() {
    let customContentItems = [
      'hero-header',
      'link',
      'mlp-column',
      'mlp-countdown',
      'mlp-group',
      'mlp-row',
      'mlp-slider',
      'mlp-voucher',
      'mlp-zatap',
    ];

    return customContentItems.includes(this.args.contentItem.type);
  }

  get optionsAttributes() {
    const attributes = Object.keys(this.args.contentItem.options).map(
      (key) => `options.${key}`,
    );
    attributes.push(
      'backgroundImage',
      'backgroundVideo',
      'backgroundVideoMobile',
      'backgroundImageMobile',
    );

    return attributes;
  }

  get isDuplicatable() {
    return (
      !this.args.contentItem.isDuplicationDisabled ||
      this.abilities.cannot('create', this.args.contentItem)
    );
  }

  get isDeletable() {
    return this.abilities.can('delete', this.args.contentItem);
  }

  get isEditable() {
    return (
      !this.args.contentItem.isPlaceholder &&
      this.abilities.can('read', this.args.contentItem)
    );
  }

  get isEmpty() {
    return this.hasChildren && this.args.contentItem.children?.length == 0;
  }

  get isReplaceable() {
    return (
      this.args.contentItem.isDefault &&
      this.abilities.can('update', this.args.contentItem)
    );
  }

  // Functions
  closeAssing = () => {
    this.scrollPositionReplace = 0;
    this.selectedContentItemToReplace = null;
  };

  closeEntity = () => {
    this.scrollPositionEntity = 0;
    this.selectedEntity = null;
  };

  duplicateContentItem = async (relation, contentItem) => {
    let copy = await this.recordCopy.copy(contentItem);
    // Find position of copy
    let currentCopyPosition = relation.indexOf(copy);
    relation.splice(currentCopyPosition, 1);

    // Move copy after original content item
    let currentPosition = relation.indexOf(contentItem);
    relation.splice(currentPosition + 1, 0, copy);
  };

  editEntity = async (contentItem) => {
    let entity =
      (await contentItem.entity) ??
      (
        await this.store.request(
          findRecord(contentItem.type, contentItem.contentItemId),
        )
      ).content.data;

    this.selectedEntity = entity;
  };

  removeContentItem = (relation, contentItem) => {
    const index = relation.indexOf(contentItem);
    if (index !== -1) {
      relation.splice(index, 1);
    }

    contentItem.deleteRecord();
  };

  replaceEntity = (entities) => {
    // Record explorer onAssignRelation returns allways an array
    entities.forEach((entity) => {
      this.selectedContentItemToReplace.headline = entity.displayHeadline;
      this.selectedContentItemToReplace.contentItemId = entity.id;
    });

    this.selectedContentItemToReplace = null;
  };

  selectContentItemToReplace = (contentItem) => {
    this.selectedContentItemToReplace = contentItem;
  };

  saveEntity = async (entity) => {
    if (this.history.isModelInvalid(entity)) {
      this.showErrors = true;
      return;
    }

    // TODO: REFACTOR-MLP-API If it's not a mlp module, save the entity before saving the mlp. Modules don't have and api endpoint
    if (!entity.isMlpModule) {
      await this.history.saveModel(entity);
    }

    // TODO: Refactor headline
    this.args.contentItem.headline = entity.displayHeadline;
    this.args.contentItem.contentItemId = entity.id;

    await this.args.onSaveMlp();

    // TODO: REFACTOR-MLP-API We need to close the options if the entity is new to avoid losing changes after a first save
    if (entity.isNew) {
      this.selectedEntity = null;
    }
  };

  toggleVisibility = (contentItem) => {
    contentItem.visible = !contentItem.visible;
  };

  registerFormModel = () => {
    let currentRoute = this.breadcrumb.currentRoute;

    if (currentRoute) {
      currentRoute.findOrRegisterFormModel(this.args.contentItem);
    }
  };

  // Template
  <template>
    <div
      class={{bem
        (hash
          has-children=this.hasChildren
          is-visible=@contentItem.isVisible
          is-empty=this.isEmpty
          is-selected=(eq @contentItem @selectedContentItem)
          type=@contentItem.type
          has-error=(and @showErrors @contentItem.validations.isInvalid)
        )
      }}
      {{sortableItem
        groupName=@sortableGroup
        disabled=@isDisabled
        model=@contentItem
      }}
      {{on 'click' (stopPropagation (fn @onSelectContentItem @contentItem))}}
      {{didInsert this.registerFormModel}}
      role='button'
      tabindex='0'
      data-test-page-builder-content-items-item={{@contentItem.type}}
      ...attributes
    >
      <div class={{bem 'header'}}>
        <div class={{bem 'handle'}} {{sortableHandle}}>
          {{svgJar 'drag' class=(bem 'drag-indicator')}}
        </div>

        <div class={{bem 'type'}}>
          {{svgJar (modelIcon @contentItem.type) class=(bem 'type-icon')}}
          {{this.contentItemTypeName}}
        </div>

        <HiddenIndicator
          @isVisible={{not @contentItem.isVisible}}
          @tooltipSide='top'
        />

        <SelectedAttributesIndicator
          @model={{@contentItem}}
          @attributes={{this.optionsAttributes}}
          @tooltip={{t
            'page_builder.content_items.item.selected_attributes_indicator'
          }}
        />

        <div class={{bem 'spacer'}}></div>

        <div class={{bem 'actions'}}>
          {{#if this.isEditable}}
            <Button
              @style='inline'
              @icon='edit'
              disabled={{@isDisabled}}
              {{on 'click' (stopPropagation (fn this.editEntity @contentItem))}}
            />
          {{/if}}

          <ContextMenu
            @isDisabled={{@isDisabled}}
            @horizontalPosition='right'
            @stopPropagation={{true}}
          >
            <:content as |content|>
              {{#if this.isReplaceable}}
                <content.Item
                  @icon='replace'
                  @onClick={{fn this.selectContentItemToReplace @contentItem}}
                  @label={{t
                    'page_builder.content_items.item.actions.replace'
                    typeName=this.contentItemTypeName
                  }}
                  @description={{t
                    'page_builder.content_items.item.actions.replace_description'
                  }}
                />
              {{/if}}

              {{#if this.isDuplicatable}}
                <content.Item
                  @icon='duplicate'
                  @onClick={{fn
                    this.duplicateContentItem
                    @contentItems
                    @contentItem
                  }}
                  @label={{t
                    'page_builder.content_items.item.actions.duplicate'
                    typeName=this.contentItemTypeName
                  }}
                />
              {{/if}}

              {{#if this.isDeletable}}
                <content.Item
                  @icon='delete'
                  @onClick={{fn
                    this.removeContentItem
                    @contentItems
                    @contentItem
                  }}
                  @label={{t
                    'page_builder.content_items.item.actions.delete'
                    typeName=this.contentItemTypeName
                  }}
                />
              {{/if}}

              <hr />

              <content.Item
                @icon={{if @contentItem.isVisible 'show' 'hide'}}
                @label={{t
                  'page_builder.content_items.item.actions.visibility'
                  typeName=this.contentItemTypeName
                }}
              >
                <Checkbox
                  @isChecked={{readonly @contentItem.isVisible}}
                  @recordType={{getRecordType @contentItem}}
                  @isDisabled={{or
                    (not @contentItem.isParentVisible)
                    (cannot 'update' @contentItem)
                  }}
                  @property='visible'
                  @onChange={{fn this.toggleVisibility @contentItem}}
                />
              </content.Item>

              {{#unless @contentItem.isParentVisible}}
                <InfoBox @type='warning' class={{bem 'invisible-hint'}}>
                  {{t
                    'page_builder.content_items.item.actions.parent_not_visible_hint'
                  }}
                </InfoBox>
              {{/unless}}
            </:content>
          </ContextMenu>
        </div>
      </div>

      {{! Custom layout for content item }}
      <this.ContentItem
        @blockName={{this.blockName}}
        @contentItem={{@contentItem}}
        @isDisabled={{@isDisabled}}
        @mlp={{@mlp}}
        @onSelectContentItem={{@onSelectContentItem}}
        @onReplaceEntity={{@onReplaceEntity}}
        @onSaveMlp={{@onSaveMlp}}
        @selectedContentItem={{@selectedContentItem}}
      />

      {{! Edit content item entity }}
      <Entity
        @contentItem={{@contentItem}}
        @entity={{this.selectedEntity}}
        @onBodyScroll={{fn (mut this.scrollPositionEntity)}}
        @onClose={{fn (mut this.selectedEntity) null}}
        @onDiscardNewEntity={{fn
          this.removeContentItem
          @contentItems
          @contentItem
        }}
        @onSaveEntity={{this.saveEntity}}
        @loadLegalContext={{@loadLegalContext}}
        @mlpArtist={{@mlp.artist}}
        @mlp={{@mlp}}
        @scrollPosition={{this.scrollPositionEntity}}
        @showErrors={{this.showErrors}}
      />

      {{! Assign overlay to replace current entity }}
      <Modal
        @onClose={{this.closeAssing}}
        @dataDependencies={{this.selectedContentItemToReplace.type}}
        as |recordType|
      >
        <this.RecordExplorerForReplace
          @filters={{@filters}}
          @recordType={{recordType}}
          @onAssignRelation={{this.replaceEntity}}
          @onBodyScroll={{fn (mut this.scrollPositionReplace)}}
          @parentRecordType='mlp'
          @relationType='belongsTo'
          @scrollPosition={{this.scrollPositionReplace}}
        />
      </Modal>
    </div>
  </template>
}
